import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { LogoUploader } from '@/components/settings/LogoUploader';
import toast from 'react-hot-toast';

interface SettingsData {
  logging: {
    app_log_level: string;
    sqlalchemy_log_level: string;
    uvicorn_log_level: string;
    audit_logging_enabled: boolean;
    log_format: string;
    log_to_file: boolean;
    log_file_path: string;
    log_file_max_size: number;
    log_file_retention_days: number;
  };
  general: {
    app_name: string;
    app_version: string;
    environment: string;
    debug_mode: boolean;
    session_timeout_minutes: number;
    max_sessions_per_user: number;
    timezone: string;
    date_format: string;
  };
}

export function GeneralSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState<SettingsData>({
    logging: {
      app_log_level: 'INFO',
      sqlalchemy_log_level: 'WARNING',
      uvicorn_log_level: 'INFO',
      audit_logging_enabled: true,
      log_format: 'detailed',
      log_to_file: false,
      log_file_path: '/var/log/netapp/app.log',
      log_file_max_size: 100,
      log_file_retention_days: 30
    },
    general: {
      app_name: 'NetApp Dashboard',
      app_version: '1.0.0',
      environment: 'development',
      debug_mode: false,
      session_timeout_minutes: 30,
      max_sessions_per_user: 5,
      timezone: 'UTC',
      date_format: 'YYYY-MM-DD HH:mm:ss'
    }
  });
  const [originalSettings, setOriginalSettings] = useState<SettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin' && user.role !== 'super_admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/v1/settings/general', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setOriginalSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch('/api/v1/settings/general', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully');
        setOriginalSettings(settings);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const response = await fetch('/api/v1/settings/apply-logging', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        toast.success('Logging settings applied successfully');
      } else {
        toast.error('Failed to apply logging settings');
      }
    } catch (error) {
      console.error('Failed to apply settings:', error);
      toast.error('Failed to apply settings');
    } finally {
      setIsApplying(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400">Loading settings...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
            { label: 'General Settings', icon: <Settings className="w-4 h-4" /> }
          ]}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              General Settings
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Configure application logging and general settings
            </p>
          </div>
        </div>

        {/* Logo Uploader */}
        <LogoUploader />

        {/* Settings Form */}
        <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-6 h-6 text-[#5869ff]" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Logging Configuration
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Application Log Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Application Log Level
              </label>
              <select
                value={settings.logging.app_log_level}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  logging: { ...prev.logging, app_log_level: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                <option value="DEBUG">DEBUG - Most verbose</option>
                <option value="INFO">INFO - General information</option>
                <option value="WARNING">WARNING - Warning messages</option>
                <option value="ERROR">ERROR - Error messages only</option>
                <option value="CRITICAL">CRITICAL - Critical only</option>
              </select>
            </div>

            {/* SQLAlchemy Log Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SQLAlchemy Log Level
              </label>
              <select
                value={settings.logging.sqlalchemy_log_level}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  logging: { ...prev.logging, sqlalchemy_log_level: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                <option value="DEBUG">DEBUG - All SQL queries</option>
                <option value="INFO">INFO - Connection pool</option>
                <option value="WARNING">WARNING - Warnings only</option>
                <option value="ERROR">ERROR - Errors only</option>
                <option value="CRITICAL">CRITICAL - Critical only</option>
              </select>
            </div>

            {/* Uvicorn Log Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Uvicorn Log Level
              </label>
              <select
                value={settings.logging.uvicorn_log_level}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  logging: { ...prev.logging, uvicorn_log_level: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                <option value="DEBUG">DEBUG - All requests</option>
                <option value="INFO">INFO - Request summary</option>
                <option value="WARNING">WARNING - Warnings only</option>
                <option value="ERROR">ERROR - Errors only</option>
                <option value="CRITICAL">CRITICAL - Critical only</option>
              </select>
            </div>

            {/* Audit Logging */}
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.logging.audit_logging_enabled}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    logging: { ...prev.logging, audit_logging_enabled: e.target.checked }
                  }))}
                  className="w-4 h-4 text-[#5869ff] bg-white dark:bg-[#1a1f5c]/30 border-gray-300 dark:border-[#5869ff]/30 rounded focus:ring-[#5869ff]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Audit Logging
                </span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                Logs all user actions and system events for compliance
              </p>
            </div>
          </div>

          {/* Alert */}
          {hasChanges && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    You have unsaved changes. Click "Save Settings" to persist your changes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg flex items-center gap-2 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Settings
            </button>

            <button
              onClick={handleApply}
              disabled={isApplying || hasChanges}
              className="px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isApplying ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Apply Without Restart
            </button>
          </div>
        </div>

        {/* Application Configuration */}
        <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-6 h-6 text-[#5869ff]" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Application Configuration
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Session Timeout */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                value={settings.general.session_timeout_minutes}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  general: { ...prev.general, session_timeout_minutes: parseInt(e.target.value) || 30 }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
                min="5"
                max="1440"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Automatically log out users after this period of inactivity
              </p>
            </div>

            {/* Max Sessions Per User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Max Sessions Per User
              </label>
              <input
                type="number"
                value={settings.general.max_sessions_per_user}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  general: { ...prev.general, max_sessions_per_user: parseInt(e.target.value) || 5 }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
                min="1"
                max="100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Maximum concurrent sessions allowed per user
              </p>
            </div>

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Timezone
              </label>
              <select
                value={settings.general.timezone}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  general: { ...prev.general, timezone: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New York</option>
                <option value="America/Chicago">America/Chicago</option>
                <option value="America/Denver">America/Denver</option>
                <option value="America/Los_Angeles">America/Los Angeles</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Europe/Paris">Europe/Paris</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
                <option value="Asia/Shanghai">Asia/Shanghai</option>
                <option value="Australia/Sydney">Australia/Sydney</option>
              </select>
            </div>

            {/* Date Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date Format
              </label>
              <select
                value={settings.general.date_format}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  general: { ...prev.general, date_format: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</option>
                <option value="MM/DD/YYYY HH:mm:ss">MM/DD/YYYY HH:mm:ss</option>
                <option value="DD/MM/YYYY HH:mm:ss">DD/MM/YYYY HH:mm:ss</option>
                <option value="DD-MM-YYYY HH:mm:ss">DD-MM-YYYY HH:mm:ss</option>
              </select>
            </div>

            {/* Debug Mode */}
            <div className="md:col-span-2">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.general.debug_mode}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    general: { ...prev.general, debug_mode: e.target.checked }
                  }))}
                  className="w-4 h-4 text-[#5869ff] bg-white dark:bg-[#1a1f5c]/30 border-gray-300 dark:border-[#5869ff]/30 rounded focus:ring-[#5869ff]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Debug Mode
                </span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                Show detailed error messages and debugging information (not recommended for production)
              </p>
            </div>
          </div>

          {/* Environment Info */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Environment Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Application</p>
                <p className="font-medium text-gray-900 dark:text-white">{settings.general.app_name}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Version</p>
                <p className="font-medium text-gray-900 dark:text-white">{settings.general.app_version}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Environment</p>
                <p className="font-medium text-gray-900 dark:text-white capitalize">{settings.general.environment}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Status</p>
                <p className="font-medium text-green-600 dark:text-green-400">Active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-[#5869ff]" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              About System Settings
            </h3>
          </div>
          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Session Management:</strong> Configure timeout and concurrent session limits for better security.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Logging Levels:</strong> Control application, database, and web server logging verbosity.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Debug Mode:</strong> Enable detailed error messages for development and troubleshooting.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Audit Logging:</strong> Track all user actions and API calls for compliance and security.
              </p>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg">
              <p className="text-yellow-800 dark:text-yellow-300">
                <strong>Note:</strong> Logging changes are applied immediately without restart. Session and debug settings require a save to take effect.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}